import { DashedLinesGap } from "features/CenterContent/RoleContent/ProductionDeviation/components/DashedLinesGap";
import { DirectionDescriptor } from "features/CenterContent/RoleContent/ProductionDeviation/components/DirectionDescriptor";
import { TrainSerieFilter } from "features/CenterContent/RoleContent/ProductionDeviation/components/FilterTrainIds";
import { ProductionDeviationForm } from "features/CenterContent/RoleContent/ProductionDeviation/types/formSchema";
import { TrainListRange } from "features/CenterContent/RoleContent/ProductionDeviation/useTrainListRange";
import {
  filterDurations,
  getAllRepresentativeTrains,
  useSelectedTrains,
  useTrainsAlreadyInProductionDeviation,
} from "features/CenterContent/RoleContent/ProductionDeviation/utils";
import { useTrainInfoContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoContext";
import {
  findSeriesDiscrepancies,
  trainDataToTrainIdAndStretch,
} from "features/History/groupTrainSeries";
import { TrainRouteDiscrepancies } from "features/VehicleSidebar/AffectedTrains/types";
import { indexBy } from "features/VehicleSidebar/VehicleList/TrainList/TrainList";
import {
  Dispatch,
  FC,
  Fragment,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  DropsTooltip,
  Field,
  Flex,
  Grid,
  SearchInput,
  Separator,
  Text,
  VStack,
} from "shared/components/Spor";
import { StyleTarget } from "shared/components/StyleTarget";
import { intersect, unique } from "shared/utils/arrayUtils";
import { toggle } from "shared/utils/objectUtils";
import { css, useTheme } from "styled-components";

type SelectTrainIdsContentProps = TrainListRange & {
  trainSerie: string;
  setRouteDiscrepancies: Dispatch<SetStateAction<TrainRouteDiscrepancies>>;
};

export const SelectTrainIdsContent: FC<SelectTrainIdsContentProps> = ({
  trainSerie,
  setRouteDiscrepancies,
  trainList,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<string[]>([]);

  const { watch } = useFormContext<ProductionDeviationForm>();

  const durations = watch("durations");
  const [selectedTrains, setSelectedTrains] = useSelectedTrains(
    trainSerie,
    trainList,
  );

  const [selectableTrains, setSelectableTrains] = useState<string[]>([]);

  const trainToTrainData = useMemo(
    () => indexBy(trainList, (train) => train.trainNumber),
    [trainList],
  );

  const { setAllTrainIds } = useTrainInfoContext();

  // Updates routeDiscrepancies and representativeTrain
  useEffect(() => {
    const trainDatas = selectedTrains.map((it) => trainToTrainData[it]);
    const trainsIdAndStretch = trainDatas.map(trainDataToTrainIdAndStretch);
    const routeDiscrepancies = findSeriesDiscrepancies(trainsIdAndStretch);
    setRouteDiscrepancies(routeDiscrepancies);

    const trainIds = trainsIdAndStretch.map((it) => it.trainId);
    const representativeTrains = getAllRepresentativeTrains(trainIds);
    setAllTrainIds(representativeTrains);
  }, [selectedTrains]);

  // Updates selected and selectable trains if date changes
  const routeOptions = useMemo(
    () =>
      unique(
        trainList.map((it) => `${it.originStopName}–${it.destinationStopName}`),
      ).toSorted(),
    [trainList],
  );

  // Updates selected and selectable trains if date changes or filters/search change
  useEffect(() => {
    if (!durations) return;
    const selectableTrainInDateRange = unique(
      filterDurations(trainList, durations),
    );
    const selectableTrainFiltered = selectableTrainInDateRange.filter(
      (trainId) => {
        if (filters.length === 0) return true;
        const td = trainToTrainData[trainId];
        const linesMatch = td.lineIds
          .map((it) => it.lineId)
          .some((it) => filters.includes(it));
        const routeLabel = `${td.originStopName}–${td.destinationStopName}`;
        const routeMatch = filters.includes(routeLabel);
        return linesMatch || routeMatch;
      },
    );
    const newSelectableTrain = selectableTrainFiltered.filter((it) =>
      it.includes(search),
    );
    setSelectableTrains(newSelectableTrain);

    if (selectedTrains.length === 0) return;

    // Intersect removes selectableTrains which are now outside the date range.
    const newSelectedTrains = intersect(
      selectedTrains,
      selectableTrainFiltered,
    );
    setSelectedTrains(newSelectedTrains);
  }, [trainSerie, search, filters]);

  // Reset filters on trainSerie switching
  useEffect(() => setFilters([]), [trainSerie]);

  const lineIds = unique(
    trainList.flatMap((it) => it.lineIds.map(({ lineId }) => lineId)),
  );

  const trainsAlreadyInProductionDeviations =
    useTrainsAlreadyInProductionDeviation(trainSerie, durations);

  return (
    <DashedLinesGap variant="secondary">
      <VStack align="stretch">
        {(lineIds.length > 1 || routeOptions.length > 1) && (
          <TrainSerieFilter
            filters={filters}
            setFilters={setFilters}
            trainList={trainList}
          />
        )}
      </VStack>

      <VStack align="stretch" flex={1}>
        <Grid
          my="12px"
          gridTemplateColumns="auto auto 1fr"
          gap="36px"
          alignItems="center"
        >
          <Flex gap="12px" alignItems="center">
            <Checkbox
              checked={selectableTrains.every((it) =>
                selectedTrains.includes(it),
              )}
              onCheckedChange={(e) => {
                let newTrains: string[];
                if (e.checked) {
                  newTrains = unique([
                    ...selectedTrains,
                    ...selectableTrains,
                  ]).toSorted();
                } else {
                  newTrains = selectedTrains.filter(
                    (it) => !selectableTrains.includes(it),
                  );
                }
                setSelectedTrains(newTrains);
              }}
            />
            <Text>{t("Velg alle")}</Text>
          </Flex>
          <Text opacity={selectedTrains.length > 0 ? 1 : 0}>
            {t("{{count}} tog valgt", { count: selectedTrains.length })}
          </Text>
          <StyleTarget
            style={{ gridColumn: 3 }}
            // https://github.com/nsbno/spor/issues/2023
            $style={css`
              label {
                font-size: 16px;
                margin-top: -4px;
                white-space: pre;
              }
              button {
                margin-right: -12px;
              }
            `}
            $target="*"
          >
            <SearchInput
              backgroundColor="transparent !important"
              borderRadius="12px"
              id="vehicle-search"
              label={t("Søk på tognummer")}
              variant="core"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onReset={() => setSearch("")}
              height="42px"
              fontSize="xs"
            />
          </StyleTarget>
        </Grid>
        {selectableTrains.length > 0 ? (
          selectableTrains.map((train) => (
            <Fragment key={train}>
              <Separator />
              <Field>
                <Flex my="1" justifyContent="space-between" alignItems="center">
                  <Flex gap="2">
                    <Checkbox
                      checked={selectedTrains.includes(train)}
                      value={train}
                      onCheckedChange={() =>
                        setSelectedTrains(
                          toggle(selectedTrains, train).toSorted(),
                        )
                      }
                    />
                    <Text fontWeight={900}>{train}</Text>
                    <DirectionDescriptor
                      originStopName={trainToTrainData[train]?.originStopName}
                      destinationStopName={
                        trainToTrainData[train]?.destinationStopName
                      }
                    />
                  </Flex>
                  {trainsAlreadyInProductionDeviations.includes(train) && (
                    <DropsTooltip
                      label={t(
                        "Dette toget eksisterer allerede i et annet produksjonsavvik i valgt periode",
                      )}
                      hasArrow
                    >
                      <Text variant="xs" color={theme.colorTextSecondary}>
                        {t("Er i produksjonsavvik")}
                      </Text>
                    </DropsTooltip>
                  )}
                </Flex>
              </Field>
            </Fragment>
          ))
        ) : (
          <Text>{t("Ingen tog som kjører i valgt tidsperiode")}</Text>
        )}
      </VStack>
    </DashedLinesGap>
  );
};
